import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { QUESTIONS, shouldSkip } from "../lib/survey-schema";
import { loadDraft, saveDraft, clearDraft } from "../lib/storage";

const EMPTY = {
    persona: null,
    pain: null,
    tools: [],
    gap: null,
    klareet_appeal: null,
    price_individual: null,
    college_price: null,
    one_thing: "",
    waitlist: null,
    email: "",
};

const SurveyContext = createContext(null);

export function SurveyProvider({ children }) {
    const [answers, setAnswers] = useState(EMPTY);
    const [index, setIndex] = useState(0); // current question index
    const [hydrated, setHydrated] = useState(false);
    const firstMount = useRef(true);

    // Hydrate once
    useEffect(() => {
        const draft = loadDraft();
        if (draft && draft.answers) {
            setAnswers({ ...EMPTY, ...draft.answers });
            if (typeof draft.index === "number") {
                setIndex(Math.min(Math.max(draft.index, 0), QUESTIONS.length - 1));
            }
        }
        setHydrated(true);
    }, []);

    // Autosave draft whenever answers or index change (after hydration)
    useEffect(() => {
        if (!hydrated) return;
        if (firstMount.current) {
            firstMount.current = false;
            return;
        }
        saveDraft({ answers, index, updatedAt: Date.now() });
    }, [answers, index, hydrated]);

    // Warn before leaving past Q2
    useEffect(() => {
        function handler(e) {
            if (index >= 2) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
            return undefined;
        }
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [index]);

    const setAnswer = useCallback((key, value) => {
        setAnswers((prev) => ({ ...prev, [key]: value }));
    }, []);

    const toggleTool = useCallback((value) => {
        setAnswers((prev) => {
            const existing = prev.tools ?? [];
            const nextTools = existing.includes(value)
                ? existing.filter((x) => x !== value)
                : [...existing, value];
            return { ...prev, tools: nextTools };
        });
    }, []);

    const nextQuestion = useCallback(() => {
        setIndex((prev) => {
            let i = prev + 1;
            while (i < QUESTIONS.length && shouldSkip(QUESTIONS[i], answers)) {
                i += 1;
            }
            return Math.min(i, QUESTIONS.length - 1);
        });
    }, [answers]);

    const previousQuestion = useCallback(() => {
        setIndex((prev) => {
            let i = prev - 1;
            while (i > 0 && shouldSkip(QUESTIONS[i], answers)) {
                i -= 1;
            }
            return Math.max(0, i);
        });
    }, [answers]);

    const reset = useCallback(() => {
        clearDraft();
        setAnswers(EMPTY);
        setIndex(0);
    }, []);

    const hasDraft = useMemo(() => {
        // A draft is "meaningful" if persona is set and at least one other answer exists.
        if (!answers.persona) return false;
        const others =
            answers.pain ||
            (answers.tools && answers.tools.length > 0) ||
            answers.gap ||
            answers.klareet_appeal != null ||
            answers.waitlist;
        return Boolean(others);
    }, [answers]);

    const isComplete = useMemo(() => {
        return Boolean(
            answers.persona &&
                answers.pain &&
                answers.klareet_appeal &&
                answers.waitlist,
        );
    }, [answers]);

    const value = {
        answers,
        setAnswer,
        toggleTool,
        index,
        setIndex,
        nextQuestion,
        previousQuestion,
        total: QUESTIONS.length,
        current: QUESTIONS[index],
        hasDraft,
        hydrated,
        isComplete,
        reset,
    };

    return (
        <SurveyContext.Provider value={value}>
            {children}
        </SurveyContext.Provider>
    );
}

export function useSurvey() {
    const ctx = useContext(SurveyContext);
    if (!ctx) {
        throw new Error("useSurvey must be used within SurveyProvider");
    }
    return ctx;
}
